/* UFORIQ location selector, v4 (2026-10-13)
 * - Persists choice to localStorage (`uforiq.location`). This key is the single source of truth.
 * - Mirrors choice into the Krown "Local" theme store-selector (localStorage["selected-store"],
 *   the selector's active item, and a `storechanged` event) so the theme's pickup availability
 *   widgets keep showing per-location stock.
 * - Reflects choice on body[data-uforiq-location] for CSS hooks
 * - Updates every chip on the page
 * - Fires `uforiq:location:changed` on window for downstream extensions (cart drawer, etc.)
 * - Auto-opens the modal on first visit if no choice stored
 *
 * v4 fix for "Nationwide doesn't stick" (cart audit 2026-10-13, issue #3). Reproduced in headless
 * Chromium with the real Krown scripts:
 *   1. v3 mirrored the choice by *clicking* Krown's store items and its "Set store" button. Krown's
 *      click handler then calls google.maps.event.trigger(map.markers[handle]), which throws for
 *      Nationwide (that store has no map pin) and before the map has loaded.
 *   2. Krown remembers the store it read at page load. When its map initialises (10 s after load,
 *      on a timer) it replays a marker click for that old store, which silently makes the old
 *      metro (for example Lubbock) Krown's active store again after the shopper chose Nationwide.
 *      Any later "Set store" commit then wrote that stale store back to selected-store.
 *   3. The area was read once at script start and never again, so a page restored from the
 *      back/forward cache, a prerendered page, or another open tab kept showing (and acting on)
 *      the previous area.
 * v4 sets Krown's state directly (no synthetic clicks, no map calls), re-asserts the choice when
 * Krown's map replays an old marker, cancels stale retries, and re-reads the saved area on
 * pageshow, tab focus and cross-tab `storage` events.
 *
 * v5 (2026-11-02, area landing):
 * - Storage and precedence live in snippets/uforiq-area-boot.liquid (window.UforiqArea, inline in <head>).
 *   get() returns the area in effect: a landing page or ?area= can set an area for this visit only
 *   (sessionStorage) when a different one is saved; an explicit choice here is always saved and wins.
 * - No first-visit dialog for crawlers (UforiqArea.BOT_RE, the same test as the age gate). A page that
 *   arrives with an area (landing page or ?area=) has one in effect, so it gets no dialog either.
 * - Writes cart attributes _delivery_area (area id) and _delivery_area_source (choice | landing | url |
 *   profile) whenever the area in effect changes, and on load when the cart lacks them. Debounced,
 *   fire-and-forget, never for crawlers.
 * - A signed-in customer's explicit choice is saved to their profile (customer metafield
 *   uforiq.delivery_area) through the uforiq-ops app proxy, POST /apps/uforiq/delivery-area.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'uforiq.location';
  var THEME_STORAGE_KEY = 'selected-store';
  var VALID = ['austin', 'lubbock', 'nationwide'];
  var LABELS = {
    austin: 'Austin Metro',
    lubbock: 'Lubbock Metro',
    nationwide: 'Nationwide'
  };
  // Default Krown store-selector block handles. Schema/snippet override per button via data-uforiq-store-handle.
  var DEFAULT_HANDLES = {
    austin: 'central-texas',
    lubbock: 'west-texas',
    nationwide: 'nationwide-shipping'
  };

  var current = null; // the area this page is currently showing
  var syncToken = 0;  // bumps on every sync so older retry loops stop

  var AREA = window.UforiqArea || null; // snippets/uforiq-area-boot.liquid; null on a layout without it

  /* The area in effect: {location, source}. */
  function read() {
    if (AREA) return AREA.effective();
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (parsed && VALID.indexOf(parsed.location) !== -1) return parsed;
    } catch (e) { /* ignore */ }
    return null;
  }

  /* Save an explicit choice. It replaces any visit-only area. */
  function write(location) {
    if (AREA) {
      AREA.clearVisit();
      return AREA.save(location, 'choice');
    }
    var payload = { location: location, choseAt: new Date().toISOString() };
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(payload)); } catch (e) { /* ignore */ }
    return payload;
  }

  function applyToDom(location) {
    current = location || null;
    document.body.setAttribute('data-uforiq-location', location || '');
    document.querySelectorAll('[data-uforiq-choice]').forEach(function (btn) {
      btn.setAttribute('aria-checked', btn.getAttribute('data-uforiq-choice') === location ? 'true' : 'false');
    });
    // 2026-10-13 redesign: the header, the mobile category row and the menu drawer each
    // render a chip. Update every chip; chips marked data-uforiq-chip-persistent stay
    // visible (showing "Choose area") before a choice is made.
    document.querySelectorAll('[data-uforiq-chip]').forEach(function (chip) {
      if (!chip.hasAttribute('data-uforiq-chip-persistent')) chip.hidden = !location;
      chip.setAttribute('data-uforiq-current', location || '');
    });
    document.querySelectorAll('[data-uforiq-chip-label]').forEach(function (chipLabel) {
      chipLabel.textContent = location ? LABELS[location] : 'Choose area';
    });
  }

  /* Klaviyo (Ethan, 2026-10-23): the area goes on the shopper's profile as "Delivery Area".
   * - window.PopupsHost: the UFORIQ pop-up adds it to an email signup made after the area was picked.
   * - A visitor Klaviyo already knows (signed up, or came from an email) gets it updated on the
   *   profile when they pick or change the area, once per session otherwise.
   * Anonymous visitors are never sent. */
  var KLAVIYO_PROPERTY = 'Delivery Area';
  var KLAVIYO_SENT_KEY = 'uforiq.location.klaviyo';

  function sendToKlaviyo(location) {
    var k = window.klaviyo;
    if (!location || !k || typeof k.identify !== 'function' || typeof k.isIdentified !== 'function') return;
    try { if (sessionStorage.getItem(KLAVIYO_SENT_KEY) === location) return; } catch (e) { /* ignore */ }
    Promise.resolve(k.isIdentified()).then(function (known) {
      if (!known) return;
      var props = {};
      props[KLAVIYO_PROPERTY] = LABELS[location];
      k.identify(props);
      try { sessionStorage.setItem(KLAVIYO_SENT_KEY, location); } catch (e) { /* ignore */ }
    }).catch(function () { /* onsite script blocked */ });
  }

  window.PopupsHost = window.PopupsHost || {};
  window.PopupsHost.properties = function () {
    var saved = read();
    var props = {};
    if (saved) props[KLAVIYO_PROPERTY] = LABELS[saved.location];
    return props;
  };

  /* Cart attributes (2026-11-02): _delivery_area + _delivery_area_source, hidden at checkout (leading
   * underscore) and copied to the order's note attributes, where uforiq-ops (orders/create) saves the area
   * to the customer's profile. /cart/update.js merges attributes, so the drawer's Delivery-Area and the
   * scheduler's Zapiet keys are left alone. */
  var CART_AREA = '_delivery_area';
  var CART_SOURCE = '_delivery_area_source';
  var CART_SOURCES = { choice: 1, landing: 1, url: 1, profile: 1 };
  var cartHas = AREA ? { area: AREA.server.cartArea || '', source: AREA.server.cartSource || '' } : null;
  var cartTimer = null;

  function cartSourceOf(eff) {
    var s = eff && eff.source;
    return CART_SOURCES[s] ? s : 'choice';
  }

  function syncCart() {
    if (!AREA || AREA.isBot || typeof fetch !== 'function') return;
    clearTimeout(cartTimer);
    cartTimer = setTimeout(function () {
      var eff = read();
      if (!eff) return;
      var want = { area: eff.location, source: cartSourceOf(eff) };
      if (cartHas.area === want.area && cartHas.source === want.source) return;
      var attrs = {};
      attrs[CART_AREA] = want.area;
      attrs[CART_SOURCE] = want.source;
      var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      try {
        fetch(root + 'cart/update.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ attributes: attrs })
        }).then(function (res) {
          if (res && res.ok) cartHas = want;
        }).catch(function () { /* the next change or page load tries again */ });
      } catch (e) { /* ignore */ }
    }, 400);
  }

  /* Signed-in customer: save an explicit choice to the profile right away (uforiq-ops app proxy). */
  var PROFILE_URL = '/apps/uforiq/delivery-area';
  function saveToProfile(location) {
    if (!AREA || !AREA.server.signedIn || AREA.isBot || typeof fetch !== 'function') return;
    if (AREA.server.profile === location) return;
    try {
      fetch(PROFILE_URL, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ area: location })
      }).then(function (res) {
        if (res && res.ok) AREA.server.profile = location;
      }).catch(function () { /* the next order saves it anyway */ });
    } catch (e) { /* ignore */ }
  }

  function dispatch(location, source) {
    sendToKlaviyo(location);
    if (location) syncCart();
    window.dispatchEvent(new CustomEvent('uforiq:location:changed', {
      detail: { location: location, source: source || 'unknown' }
    }));
  }

  function resolveStoreHandle(location) {
    var btn = document.querySelector('[data-uforiq-choice="' + location + '"][data-uforiq-store-handle]');
    if (btn) {
      var handle = btn.getAttribute('data-uforiq-store-handle');
      if (handle) return handle;
    }
    return DEFAULT_HANDLES[location] || null;
  }

  function mainSelector() {
    return document.querySelector('store-selector[data-main-selector]');
  }

  /* Put Krown's <store-selector> into the same state its own "Set store" button would, without
   * clicking anything: localStorage["selected-store"], the active item + checkbox, the header
   * label (changeSelectedStoreHandleContent) and the `storechanged` event that the pickup
   * availability widgets listen to. Returns false when the custom element isn't upgraded yet. */
  function applyToKrown(handle, notify) {
    var selectorEl = mainSelector();
    var ssDefined = typeof customElements !== 'undefined' && customElements.get && !!customElements.get('store-selector');
    if (!selectorEl || !ssDefined) return false;
    var item = selectorEl.querySelector('.store-selector-item[data-handle="' + handle + '"]');
    if (!item) return true; // that store isn't configured in the theme; localStorage is already set
    var changed = false;
    selectorEl.querySelectorAll('.store-selector-item').forEach(function (el) {
      var on = el === item;
      if (el.classList.contains('store-selector-item--store--active') !== on) changed = true;
      el.classList.toggle('store-selector-item--store--active', on);
      var input = el.querySelector('input');
      if (input) input.checked = on;
      var tab = el.querySelector('.js-accordion-tab');
      if (tab) tab.setAttribute('aria-selected', on ? 'true' : 'false');
    });
    var setBtn = document.querySelector('[data-js-select-store]');
    if (setBtn) {
      setBtn.style.display = 'block';
      setBtn.classList.add('show-change-label');
      setBtn.classList.remove('show-select-label');
    }
    if (typeof selectorEl.changeSelectedStoreHandleContent === 'function') {
      try { selectorEl.changeSelectedStoreHandleContent(handle); } catch (e) { /* header label is optional */ }
    }
    if (notify || changed) selectorEl.dispatchEvent(new CustomEvent('storechanged'));
    return true;
  }

  function syncToThemeStoreSelector(location, notify) {
    var handle = resolveStoreHandle(location);
    if (!handle) return;
    try { localStorage.setItem(THEME_STORAGE_KEY, handle); } catch (e) { /* ignore */ }
    var token = ++syncToken;
    (function attempt(n) {
      if (token !== syncToken) return; // a newer choice took over
      if (applyToKrown(handle, notify)) return;
      // The <store-selector> isn't upgraded yet (deferred script); localStorage is already set,
      // so the next page load is right either way. Retry a few times for this page.
      if (n < 30) setTimeout(function () { attempt(n + 1); }, 100);
    })(0);
  }

  /* Krown's map replays a marker click for the store it saw at page load (component-store-selector:
   * map "ready" -> setTimeout -> marker click), which flips Krown's active store back to an old
   * metro. Nothing in the redesign lets a shopper click those markers, so put our choice back. */
  function guardKrownMapReplay() {
    var selectorEl = mainSelector();
    if (!selectorEl || !selectorEl.dataset.mapSelector) return;
    var map = document.getElementById(selectorEl.dataset.mapSelector);
    if (!map || map.__uforiqGuard) return;
    map.__uforiqGuard = true;
    map.addEventListener('marker', function (e) {
      var saved = read(); // the area in effect
      if (!saved) return;
      var handle = resolveStoreHandle(saved.location);
      if (e.detail && e.detail.store === handle) return;
      setTimeout(function () { applyToKrown(handle, false); }, 0);
    });
  }

  /* An explicit choice (dialog, chip, age gate, delivery strip, the scheduler's ZIP switch): saved, wins
   * over any landing page, and goes to a signed-in customer's profile. */
  function setLocation(location, source) {
    if (VALID.indexOf(location) === -1) return;
    write(location);
    applyToDom(location);
    syncToThemeStoreSelector(location, true);
    dispatch(location, source);
    saveToProfile(location);
    closeModal();
  }

  /* Re-read the saved area (another tab, a back/forward-cache restore or a prerendered page may
   * be showing an older one) and update this page if it differs. */
  function refreshFromStorage(source) {
    var saved = read();
    var loc = saved ? saved.location : null;
    if (loc === current) return;
    applyToDom(loc);
    if (loc) syncToThemeStoreSelector(loc, true);
    dispatch(loc, source);
  }

  function getModal() { return document.querySelector('[data-uforiq-modal]'); }

  function openModal() {
    var m = getModal();
    if (!m) return;
    if (typeof m.showModal === 'function') {
      if (!m.open) m.showModal();
    } else m.setAttribute('open', '');
  }

  function closeModal() {
    var m = getModal();
    if (!m) return;
    if (typeof m.close === 'function') m.close();
    else m.removeAttribute('open');
  }

  function bind() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-uforiq-choice]');
      if (btn) {
        e.preventDefault();
        setLocation(btn.getAttribute('data-uforiq-choice'), 'button');
        return;
      }
      if (e.target.closest('[data-uforiq-chip]')) {
        e.preventDefault();
        openModal();
        return;
      }
      if (e.target.closest('[data-uforiq-modal-close]')) {
        e.preventDefault();
        closeModal();
        return;
      }
    });
    window.addEventListener('storage', function (e) {
      if (e.key === STORAGE_KEY || e.key === null) refreshFromStorage('storage');
    });
    window.addEventListener('pageshow', function (e) {
      if (e.persisted) refreshFromStorage('pageshow');
    });
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'visible') refreshFromStorage('visible');
    });
    document.addEventListener('prerenderingchange', function () { refreshFromStorage('prerender'); });
  }

  function locationFromThemeHandle(handle) {
    if (!handle) return null;
    for (var i = 0; i < VALID.length; i++) {
      if (resolveStoreHandle(VALID[i]) === handle) return VALID[i];
    }
    return null;
  }

  function isBot() {
    if (AREA) return AREA.isBot;
    return /bot|crawl|spider|lighthouse|google-inspectiontool/i.test(navigator.userAgent || '');
  }

  function init() {
    bind();
    guardKrownMapReplay();
    var stored = read();
    if (stored) {
      // A landing page or ?area= that changed the area on this load reports its own source.
      var boot = AREA && AREA.boot;
      var source = boot && boot.incoming && boot.after !== boot.before ? boot.incoming.source : 'restore';
      applyToDom(stored.location);
      syncToThemeStoreSelector(stored.location, false);
      dispatch(stored.location, source);
      return;
    }
    // No uforiq choice — try to adopt the theme's existing store-selector value.
    var themeHandle = null;
    try { themeHandle = localStorage.getItem(THEME_STORAGE_KEY); } catch (e) { /* ignore */ }
    var adopted = locationFromThemeHandle(themeHandle);
    if (adopted) {
      write(adopted);
      applyToDom(adopted);
      dispatch(adopted, 'theme-adopt');
      return;
    }
    applyToDom(null);
    // 2026-11-02: never for crawlers. The age gate skips them, so this dialog used to open for Googlebot.
    if (isBot()) return;
    // 2026-10-12: when the UFORIQ age gate is showing, it runs the location step itself
    // (snippets/uforiq-age-gate.liquid), so don't open this dialog on top of it.
    if (document.documentElement.classList.contains('uqag-on')) return;
    // First-visit prompt — wait a beat so the page paints first.
    setTimeout(openModal, 600);
  }

  // Public API for other scripts (per-location availability extension, cart drawer, analytics, etc.)
  window.UforiqLocation = {
    get: function () { var s = read(); return s ? s.location : null; },
    // How the area in effect was set: choice | landing | url | profile (null when none).
    source: function () { var s = read(); return s ? cartSourceOf(s) : null; },
    set: function (location, source) { setLocation(location, source || 'api'); },
    open: openModal,
    close: closeModal,
    LABELS: LABELS
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
